import React, {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';
import { hasValue } from 'swipeclock-frontend';

const LOGIN_PAGE_STATE = Object.freeze({
  START: 'START',
  FORGOTPASS: 'FORGOTPASS',
});

export const FORM_IDS = Object.freeze({
  USERNAME: 'username',
  PASSWORD: 'password',
});

export const formDefaultValues = {
  username: '',
  password: '',
};

const LoginContext = createContext();

export function useLoginContext() {
  return useContext(LoginContext);
}

const initialState = {
  currentPage: LOGIN_PAGE_STATE.START,
  errors: {},
  globalMessage: undefined,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
    case 'SET_ERRORS':
      return {
        ...state,
        errors: { ...state.errors, ...action.payload },
      };
    case 'SET_GLOBAL_MESSAGE':
      return {
        ...state,
        globalMessage: action.payload,
      };
    default:
      return state;
  }
};

export default function LoginProvider({ children, defaultValues: bag }) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    formBag: bag ?? initialState.formBag,
  });

  const { pages, totalPages } = useMemo(() => {
    return {
      pages: LOGIN_PAGE_STATE,
      totalPages: Object.keys(LOGIN_PAGE_STATE).length,
    };
  }, [Object.keys(LOGIN_PAGE_STATE).length]);

  const setCurrentPage = useCallback((page) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  }, []);

  const setError = useCallback((id = null, error = undefined) => {
    const payload = hasValue(id) ? { [id]: error } : {};
    dispatch({ type: 'SET_ERRORS', payload });
  }, []);

  /**
   * variable passed here will either be undefined or contain an object with
   * message: string
   * severity: string
   */
  const setGlobalMessage = useCallback((object = undefined) => {
    dispatch({ type: 'SET_GLOBAL_MESSAGE', payload: object });
  }, []);

  const reset = useCallback(() => {
    setError();
    setCurrentPage(LOGIN_PAGE_STATE.START);
  }, []);

  // Note on useMemo:
  // The state reference remains the same across renders
  // So using "state" is suitable for useHook dependency arrays
  // like useMemo here.
  const value = useMemo(
    () => ({
      state,
      actions: {
        reset,
        setCurrentPage,
        setError,
        setGlobalMessage,
      },
      meta: {
        pages,
        totalPages,
      },
    }),
    [state]
  );

  return (
    <LoginContext.Provider value={value}>{children}</LoginContext.Provider>
  );
}

LoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultValues: PropTypes.object,
};
